// opervex-frontend/src/components/Filter/FilterAccordion.js

import { useState, useEffect } from "react";
import { Button, Form, InputGroup, Accordion, Row, Col } from "react-bootstrap";
import { FaTrash } from "react-icons/fa";

import CurrencyInput from "../../components/CurrencyInput";
import useOpervex from "../../hooks/useOpervex";
import { LOGIC_OPERATORS, FILTER_OPTIONS } from "../../constants/filters";

const FilterAccordion = ({ autoFilter, layout, setData, urlData, preFilters, maxLines, noFilter }) => {
  const [maxRows, setMaxRows] = useState(maxLines || 500);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState([]);
  const [filtersHidden, setFiltersHidden] = useState([]);

  const OpervexAPI = useOpervex();

  const visibleFields = layout?.filter((col) => col.visible && !col.hidden) || [];

  /*
   * Converte os filtros para o formato esperado pela API.
   */
  const buildFilterBody = (filterList) =>
    filterList
      .filter((filter) => filter.field)
      .map(({ field, operator, value, logic }, index) => ({
        operation: index === 0 ? "AND" : logic || "AND",
        field,
        condition: operator,
        value,
      }));

  /*
   * Executa o filtro.
   *
   * É possível informar os filtros diretamente para evitar
   * problemas com atualização assíncrona do estado do React.
   */
  const handleFiltrar = async (currentFilters = filters, currentHiddenFilters = filtersHidden, currentMaxRows = maxRows) => {
    if (!layout?.length) return;

    setLoading(true);

    const filterBody = buildFilterBody(currentFilters);

    const filterBodyHidden = buildFilterBody(currentHiddenFilters);

    let body = [];

    if (filterBodyHidden.length > 0) {
      body = [...filterBodyHidden];

      if (filterBody.length > 0) {
        body.push({
          subquery: filterBody,
        });
      }
    } else {
      body = filterBody;
    }

    try {
      const result = await OpervexAPI.Axios.post(`${urlData}?limit=${currentMaxRows}`, body);

      setData(result);
    } catch (err) {
      console.error("Erro ao carregar os dados:", err);
    } finally {
      setLoading(false);
    }
  };

  /*
   * Inicializa filtros quando o layout estiver disponível.
   */
  useEffect(() => {
    if (!layout?.length) return;

    const initializeFilters = async () => {
      const initialMaxRows = maxLines || 500;

      setMaxRows(initialMaxRows);

      // ================================
      // COM PRÉ-FILTROS
      // ================================
      if (preFilters?.length) {
        const newFilters = [];
        const newHiddenFilters = [];

        preFilters.forEach((preFilter) => {
          const item = layout.find((col) => col.key === preFilter.fieldname);

          if (!item) return;

          const operator = preFilter.operator || FILTER_OPTIONS[item.type]?.[0]?.value || "=";

          const filter = {
            field: preFilter.fieldname,
            operator,
            value: preFilter.value ?? "",
            logic: preFilter.logic || "AND",
            type: item.type,
          };

          if (item.hidden) {
            newHiddenFilters.push({
              ...filter,
              logic: newHiddenFilters.length ? filter.logic : "",
            });
          } else {
            newFilters.push({
              ...filter,
              logic: newFilters.length ? filter.logic : "",
            });
          }
        });

        setFilters(newFilters);
        setFiltersHidden(newHiddenFilters);

        // Consulta inicial COM os pré-filtros
        await handleFiltrar(newFilters, newHiddenFilters, initialMaxRows);

        return;
      }

      // ================================
      // SEM PRÉ-FILTROS
      // ================================

      setFilters([]);
      setFiltersHidden([]);

      // Consulta inicial SEM filtros
      await handleFiltrar([], [], initialMaxRows);
    };

    initializeFilters();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout]);

  /*
   * Adiciona um novo filtro usando o primeiro
   * campo visível disponível.
   */
  const handleAddFilter = () => {
    const item = visibleFields[0];

    if (!item) return;

    const options = FILTER_OPTIONS[item.type] || [];

    const defaultOperator = options[0]?.value || "=";

    setFilters((prev) => [
      ...prev,
      {
        field: item.key,
        operator: defaultOperator,
        value: "",
        logic: prev.length ? "AND" : "",
        type: item.type,
      },
    ]);
  };

  /*
   * Remove um filtro e garante que o primeiro
   * não tenha operador lógico visual.
   */
  const handleRemoveFilter = (index) => {
    setFilters((prev) =>
      prev
        .filter((_, i) => i !== index)
        .map((filter, i) => ({
          ...filter,
          logic: i === 0 ? "" : filter.logic || "AND",
        })),
    );
  };

  /*
   * Altera um filtro.
   *
   * Ao trocar o campo:
   * - atualiza o tipo;
   * - redefine o operador;
   * - limpa o valor anterior.
   */
  const handleChangeFilter = (index, key, value) => {
    setFilters((prev) =>
      prev.map((filter, i) => {
        if (i !== index) return filter;

        if (key === "field") {
          const item = layout.find((col) => col.key === value);

          if (!item) {
            return {
              ...filter,
              field: value,
            };
          }

          const options = FILTER_OPTIONS[item.type] || [];

          return {
            ...filter,
            field: value,
            type: item.type,
            operator: options[0]?.value || "=",
            value: "",
          };
        }

        return {
          ...filter,
          [key]: value,
        };
      }),
    );
  };

  /*
   * Renderiza o input conforme o tipo do campo.
   */
  const renderFilterInput = (filter, index) => {
    switch (filter.type) {
      case "boolean":
        return (
          <Form.Select value={filter.value} onChange={(e) => handleChangeFilter(index, "value", e.target.value)} size="sm">
            <option value="">Selecione</option>

            <option value="true">Verdadeiro</option>

            <option value="false">Falso</option>
          </Form.Select>
        );

      case "currency":
        return <CurrencyInput value={filter.value} onChange={(e) => handleChangeFilter(index, "value", e.target.value)} />;

      case "date":
      case "dateOnly":
        return (
          <Form.Control
            type="date"
            value={filter.value}
            onChange={(e) => handleChangeFilter(index, "value", e.target.value)}
            size="sm"
          />
        );

      case "number":
      case "integer":
        return (
          <Form.Control
            type="number"
            step="1"
            value={filter.value}
            onChange={(e) => handleChangeFilter(index, "value", e.target.value)}
            size="sm"
          />
        );

      case "decimal":
        return (
          <Form.Control
            type="number"
            step="any"
            value={filter.value}
            onChange={(e) => handleChangeFilter(index, "value", e.target.value)}
            size="sm"
          />
        );

      default:
        return (
          <Form.Control
            type="text"
            value={filter.value}
            onChange={(e) => handleChangeFilter(index, "value", e.target.value)}
            size="sm"
          />
        );
    }
  };

  return (
    <Accordion defaultActiveKey="0" className="mb-2" hidden={noFilter}>
      <Accordion.Item eventKey="0">
        <Accordion.Header>Filtros</Accordion.Header>

        <Accordion.Body>
          {!autoFilter &&
            filters.map((filter, index) => {
              const operatorOptions = FILTER_OPTIONS[filter.type] || [];

              return (
                <Row className="mb-1" key={index}>
                  <InputGroup className="p-0" size="sm">
                    {/* Operador lógico */}
                    <Col sm="1" className="px-1">
                      {index > 0 && (
                        <Form.Select
                          value={filter.logic}
                          onChange={(e) => handleChangeFilter(index, "logic", e.target.value)}
                          size="sm"
                        >
                          {LOGIC_OPERATORS.map((op) => (
                            <option key={op.value} value={op.value}>
                              {op.label}
                            </option>
                          ))}
                        </Form.Select>
                      )}
                    </Col>

                    {/* Campo */}
                    <Col sm="3" className="px-1">
                      <Form.Select
                        value={filter.field}
                        onChange={(e) => handleChangeFilter(index, "field", e.target.value)}
                        size="sm"
                      >
                        {visibleFields.map((col) => (
                          <option key={col.key} value={col.key}>
                            {col.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>

                    {/* Operador */}
                    <Col sm="2" className="px-1">
                      <Form.Select
                        value={filter.operator}
                        onChange={(e) => handleChangeFilter(index, "operator", e.target.value)}
                        size="sm"
                      >
                        {operatorOptions.map((op) => (
                          <option key={op.value} value={op.value}>
                            {op.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>

                    {/* Valor */}
                    <Col sm="5" className="px-1">
                      {renderFilterInput(filter, index)}
                    </Col>

                    {/* Remover */}
                    <Col sm="1" className="px-1">
                      <Button variant="outline-danger" size="sm" onClick={() => handleRemoveFilter(index)}>
                        <FaTrash />
                      </Button>
                    </Col>
                  </InputGroup>
                </Row>
              );
            })}

          <Row className="mb-2" />

          <div className="d-flex justify-content-between align-items-center mb-2">
            {/* Quantidade máxima de linhas */}
            <Form.Group as={Row} size="sm">
              <Col sm="6">
                <Form.Label>Linhas</Form.Label>
              </Col>

              <Col sm="6">
                <Form.Control
                  type="number"
                  size="sm"
                  value={maxRows}
                  onChange={(e) => setMaxRows(e.target.value)}
                  style={{
                    maxWidth: "75px",
                    width: "75px",
                  }}
                />
              </Col>
            </Form.Group>

            {/* Botões */}
            <div className="text-end">
              <Button
                variant="outline-success"
                onClick={handleAddFilter}
                size="sm"
                hidden={autoFilter}
                disabled={!visibleFields.length}
              >
                Adicionar Filtro
              </Button>{" "}
              <Button
                variant="primary"
                onClick={() => handleFiltrar()}
                disabled={!layout || layout.length === 0 || loading}
                size="sm"
              >
                {loading ? "Carregando..." : "Filtrar"}
              </Button>
            </div>
          </div>
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
};

export default FilterAccordion;
